import { useEffect, useMemo, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info, Plus, Trash2, AlertTriangle, Check, FileUp, Loader2, Save, Undo2 } from 'lucide-react';
import { api, type FacturaLuzLeida, type LecturasLuz } from '@/lib/api';

// --- Valores de partida ---
// Los del «Resumen de la factura» de Lucera de marzo de 2026, la factura con la
// que se montó la calculadora. Cada mes se sobrescriben con los de la nueva.
const FACTURA_INICIAL = {
  potencia: '14,91',
  cuota: '0,00',
  otros: '1,41',
  energia: '75,62',
  impuesto: '0,60',
  iva: '10',
  kwh: '602',
};

type Factura = typeof FACTURA_INICIAL;

/**
 * Un inquilino es su contador: lo que paga por consumo sale de la diferencia
 * entre la lectura actual y la anterior. La anterior viene de lo guardado el
 * mes pasado; la primera vez se escribe a mano (la lectura inicial).
 */
interface Inquilino {
  id: string;
  nombre: string;
  anterior: string;
  actual: string;
  /** Si paga su parte de los costes fijos. Un local vacío sigue leyendo contador, pero no paga potencia. */
  pagaFijos: boolean;
}

type RepartoDiferencia = 'consumo' | 'iguales';

type Lectura =
  | { estado: 'nada' }
  | { estado: 'leyendo'; nombre: string }
  | { estado: 'ok'; factura: FacturaLuzLeida }
  | { estado: 'error'; mensaje: string };

/** Mensaje tras guardar o deshacer; `donde` lo pone junto al botón que se ha pulsado. */
type Aviso = { tipo: 'ok' | 'error'; texto: string; donde: 'contadores' | 'reparto' } | null;

const INQUILINOS_INICIALES: Inquilino[] = [1, 2, 3].map((i) => ({
  id: `c${i}`,
  nombre: `Inquilino ${i}`,
  anterior: '',
  actual: '',
  pagaFijos: true,
}));

// --- Helpers ---
// Acepta coma decimal («14,91») y punto de miles si va con coma («1.234,5»).
// Vacío cuenta como 0; lo que no es un número devuelve null.
function leer(s: string): number | null {
  const t = s.trim();
  if (t === '') return 0;
  const normal = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  if (!/^-?\d*\.?\d*$/.test(normal) || normal === '-' || normal === '.') return null;
  return parseFloat(normal);
}

const num = (s: string) => leer(s) ?? 0;

const eur = (n: number) =>
  n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Número para una casilla: coma decimal y sin punto de miles. */
const aCasilla = (n: number, decimales = 2) =>
  n.toLocaleString('es-ES', { minimumFractionDigits: decimales, maximumFractionDigits: 2, useGrouping: false });

const kwhTxt = (n: number) => `${n.toLocaleString('es-ES', { maximumFractionDigits: 2 })} kWh`;

const lecturaTxt = (n: number) => n.toLocaleString('es-ES', { maximumFractionDigits: 2 });

/** kWh entre dos lecturas; null si falta alguna o no es un número. Puede salir negativo. */
function consumoDe(q: Inquilino): number | null {
  if (q.anterior.trim() === '' || q.actual.trim() === '') return null;
  const a = leer(q.anterior);
  const b = leer(q.actual);
  if (a === null || b === null) return null;
  // Las lecturas llevan decimales: sin redondear, 12345,6 − 12145,2 da 200,40000000000146.
  return Math.round((b - a) * 1000) / 1000;
}

const nuevoId = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Redondea a céntimos unos importes de forma que sumen exactamente `total`
// céntimos: el céntimo que sobra o falta va a quien más se acercaba a recibirlo.
function repartirCentimos(exactos: number[], total: number): number[] {
  const redondos = exactos.map(Math.round);
  let diff = total - redondos.reduce((a, b) => a + b, 0);
  if (diff === 0 || exactos.length === 0) return redondos;
  const paso = Math.sign(diff);
  const orden = exactos
    .map((_, i) => i)
    .sort((a, b) => paso * ((exactos[b] - redondos[b]) - (exactos[a] - redondos[a])));
  for (let k = 0; diff !== 0; k = (k + 1) % orden.length) {
    redondos[orden[k]] += paso;
    diff -= paso;
  }
  return redondos;
}

// --- Component ---
export default function CalculadoraLuzPage() {
  const [factura, setFactura] = useState<Factura>(FACTURA_INICIAL);
  const [inquilinos, setInquilinos] = useState<Inquilino[]>(INQUILINOS_INICIALES);
  const [modoDiferencia, setModoDiferencia] = useState<RepartoDiferencia>('consumo');
  const [lectura, setLectura] = useState<Lectura>({ estado: 'nada' });
  const [arrastrando, setArrastrando] = useState(false);

  // Lecturas guardadas en el servidor. null mientras cargan o si no se pudo.
  const [guardadas, setGuardadas] = useState<LecturasLuz | null>(null);
  const [carga, setCarga] = useState<'cargando' | 'ok' | 'error'>('cargando');
  const [periodo, setPeriodo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState<Aviso>(null);

  useEffect(() => {
    api
      .getLecturasLuz()
      .then((doc) => {
        setGuardadas(doc);
        if (doc.contadores.length) {
          setInquilinos(
            doc.contadores.map((c) => ({
              id: c.id,
              nombre: c.nombre,
              anterior: aCasilla(c.lectura, 0),
              actual: '',
              pagaFijos: c.pagaFijos ?? true,
            })),
          );
        }
        setCarga('ok');
      })
      .catch(() => setCarga('error'));
  }, []);

  const leerPdf = async (fichero: File | undefined) => {
    if (!fichero || lectura.estado === 'leyendo') return;
    setLectura({ estado: 'leyendo', nombre: fichero.name });
    try {
      const f = await api.leerFacturaLuz(fichero);
      setFactura({
        potencia: aCasilla(f.potencia),
        cuota: aCasilla(f.cuota),
        otros: aCasilla(f.otros),
        energia: aCasilla(f.energia),
        impuesto: aCasilla(f.impuesto),
        iva: aCasilla(f.ivaPct, 0),
        kwh: f.kwh !== null ? aCasilla(f.kwh, 0) : '',
      });
      if (f.periodo) setPeriodo(f.periodo);
      setLectura({ estado: 'ok', factura: f });
    } catch (e) {
      setLectura({ estado: 'error', mensaje: e instanceof Error ? e.message : 'No se ha podido leer la factura' });
    }
  };

  const soltar = (e: DragEvent) => {
    e.preventDefault();
    setArrastrando(false);
    leerPdf(e.dataTransfer.files[0]);
  };

  const campo = (k: keyof Factura) => (v: string) => setFactura((f) => ({ ...f, [k]: v }));

  const cambiarInquilino = (id: string, cambios: Partial<Inquilino>) =>
    setInquilinos((lista) => lista.map((q) => (q.id === id ? { ...q, ...cambios } : q)));

  const anadirInquilino = () =>
    setInquilinos((lista) => [
      ...lista,
      { id: nuevoId(), nombre: `Inquilino ${lista.length + 1}`, anterior: '', actual: '', pagaFijos: true },
    ]);

  const quitarInquilino = (id: string) => setInquilinos((lista) => lista.filter((q) => q.id !== id));

  const consumos = inquilinos.map(consumoDe);

  const r = useMemo(() => {
    // Todo en céntimos para que los totales cuadren con la factura al céntimo.
    const fijos = Math.round((num(factura.potencia) + num(factura.cuota) + num(factura.otros)) * 100);
    const variables = Math.round((num(factura.energia) + num(factura.impuesto)) * 100);
    const ivaPct = num(factura.iva) / 100;
    const base = fijos + variables;
    const iva = Math.round(base * ivaPct);
    const kwhFactura = num(factura.kwh);

    const n = inquilinos.length;
    const kwhs = inquilinos.map((q) => Math.max(0, consumoDe(q) ?? 0));
    const kwhTotal = kwhs.reduce((a, b) => a + b, 0);
    const diferenciaKwh = kwhFactura > 0 ? kwhFactura - kwhTotal : 0;
    const hayDiferencia = kwhTotal > 0 && Math.abs(diferenciaKwh) > 0.005;
    // Repartir a partes iguales solo tiene sentido si falta consumo por asignar
    // (zonas comunes, un contador que no se ha leído…); si los contadores suman
    // más que la factura, se reparte siempre en proporción.
    // Los que pagan fijos son los que comparten el suministro: entre ellos se
    // reparten los fijos y, si se elige, el consumo sin asignar (zonas comunes).
    const pagadores = inquilinos.map((q, i) => (q.pagaFijos ? i : -1)).filter((i) => i >= 0);
    const repartoIguales = modoDiferencia === 'iguales' && diferenciaKwh > 0 && pagadores.length > 0;

    const exactosConsumo = kwhs.map((k, i) => {
      if (kwhTotal === 0) return 0;
      if (repartoIguales) {
        const precio = variables / kwhFactura;
        const comunes = inquilinos[i].pagaFijos ? (precio * diferenciaKwh) / pagadores.length : 0;
        return precio * k + comunes;
      }
      return (variables * k) / kwhTotal;
    });

    // Los fijos, a partes iguales entre los que los pagan; los demás, cero.
    const fijoCada = Array<number>(n).fill(0);
    if (pagadores.length) {
      repartirCentimos(Array(pagadores.length).fill(fijos / pagadores.length), fijos).forEach((c, j) => {
        fijoCada[pagadores[j]] = c;
      });
    }
    const consumoCada = repartirCentimos(exactosConsumo, variables);
    const baseCada = fijoCada.map((f, i) => f + consumoCada[i]);
    const ivaCada = repartirCentimos(baseCada.map((b) => b * ivaPct), iva);

    const filas = inquilinos.map((q, i) => ({
      id: q.id,
      nombre: q.nombre.trim() || `Inquilino ${i + 1}`,
      pagaFijos: q.pagaFijos,
      kwh: kwhs[i],
      pctConsumo: kwhTotal > 0 ? kwhs[i] / kwhTotal : 0,
      fijo: fijoCada[i] / 100,
      consumo: consumoCada[i] / 100,
      iva: ivaCada[i] / 100,
      total: (baseCada[i] + ivaCada[i]) / 100,
    }));

    return {
      fijos: fijos / 100,
      pagadores: pagadores.length,
      variables: variables / 100,
      base: base / 100,
      iva: iva / 100,
      total: (base + iva) / 100,
      precioKwh: kwhFactura > 0 ? variables / 100 / kwhFactura : kwhTotal > 0 ? variables / 100 / kwhTotal : 0,
      kwhFactura,
      kwhTotal,
      diferenciaKwh,
      hayDiferencia,
      filas,
      sumaFilas: filas.reduce((a, f) => a + Math.round(f.total * 100), 0) / 100,
    };
  }, [factura, inquilinos, modoDiferencia]);

  const n = inquilinos.length;
  const nombreDe = (i: number) => inquilinos[i].nombre.trim() || `Inquilino ${i + 1}`;
  const hayInvalidos = [
    ...Object.values(factura),
    ...inquilinos.flatMap((q) => [q.anterior, q.actual]),
  ].some((v) => leer(v) === null);
  const sinAnterior = inquilinos.map((q, i) => (q.anterior.trim() === '' ? nombreDe(i) : null)).filter(Boolean);
  const sinActual = inquilinos.map((q, i) => (q.actual.trim() === '' ? nombreDe(i) : null)).filter(Boolean);
  const negativos = consumos.map((c, i) => (c !== null && c < 0 ? nombreDe(i) : null)).filter(Boolean);
  const repartoListo =
    !hayInvalidos &&
    sinAnterior.length === 0 &&
    sinActual.length === 0 &&
    negativos.length === 0 &&
    r.pagadores > 0 &&
    r.kwhTotal > 0;

  const primeraVez = carga === 'ok' && guardadas !== null && guardadas.contadores.length === 0 && guardadas.cierres.length === 0;
  const ultimoCierre = guardadas?.cierres[guardadas.cierres.length - 1];
  const yaGuardado =
    ultimoCierre !== undefined && periodo.trim() !== '' && ultimoCierre.periodo.trim().toLowerCase() === periodo.trim().toLowerCase();

  const guardarIniciales = async () => {
    setGuardando(true);
    setAviso(null);
    try {
      const doc = await api.guardarLecturasLuz(
        inquilinos.map((q, i) => ({ id: q.id, nombre: nombreDe(i), lectura: num(q.anterior), pagaFijos: q.pagaFijos })),
      );
      setGuardadas(doc);
      setAviso({
        tipo: 'ok',
        texto: 'Guardadas las lecturas iniciales. Desde ahora, cada mes solo hay que escribir la actual.',
        donde: 'contadores',
      });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error ? e.message : 'No se han podido guardar las lecturas', donde: 'contadores' });
    } finally {
      setGuardando(false);
    }
  };

  const cerrarMes = async () => {
    setGuardando(true);
    setAviso(null);
    const deLaFactura = lectura.estado === 'ok' ? lectura.factura : null;
    try {
      const doc = await api.cerrarMesLuz({
        periodo: periodo.trim(),
        desde: deLaFactura?.desde ?? null,
        hasta: deLaFactura?.hasta ?? null,
        totalFactura: r.total,
        lineas: inquilinos.map((q, i) => ({
          contadorId: q.id,
          nombre: r.filas[i].nombre,
          anterior: num(q.anterior),
          actual: num(q.actual),
          pagaFijos: q.pagaFijos,
          importe: r.filas[i].total,
        })),
      });
      setGuardadas(doc);
      setAviso({
        tipo: 'ok',
        texto: `Guardadas las lecturas de ${periodo.trim()}. El mes que viene, la lectura anterior de cada contador será la de hoy.`,
        donde: 'reparto',
      });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error ? e.message : 'No se han podido guardar las lecturas', donde: 'reparto' });
    } finally {
      setGuardando(false);
    }
  };

  const deshacer = async () => {
    if (!ultimoCierre) return;
    const ok = window.confirm(
      `¿Deshacer las lecturas de ${ultimoCierre.periodo}? Los contadores vuelven a las lecturas con las que empezó ese mes y sale del historial.`,
    );
    if (!ok) return;
    setGuardando(true);
    setAviso(null);
    try {
      const doc = await api.deshacerMesLuz();
      setGuardadas(doc);
      // Se dejan puestas las lecturas de ese mes para corregirlas y volver a guardar.
      setInquilinos(
        ultimoCierre.lineas.map((l) => ({
          id: l.contadorId,
          nombre: l.nombre,
          anterior: aCasilla(l.anterior, 0),
          actual: aCasilla(l.actual, 0),
          pagaFijos: l.pagaFijos ?? true,
        })),
      );
      setPeriodo(ultimoCierre.periodo);
      setAviso({
        tipo: 'ok',
        texto: `Deshecho ${ultimoCierre.periodo}. Corrige las lecturas y vuelve a guardarlas; si no tienes su factura cargada, súbela antes.`,
        donde: 'reparto',
      });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e instanceof Error ? e.message : 'No se ha podido deshacer', donde: 'reparto' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div>
        <Link
          to="/calculadoras"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sereno-dark transition-colors mb-3"
        >
          <ArrowLeft size={16} /> Calculadoras
        </Link>
        <h1 className="text-2xl font-bold text-sereno-dark">Reparto de la factura de luz</h1>
        <p className="text-slate-500 mt-1">
          Los costes fijos se dividen a partes iguales y la energía según lo que marca el contador de cada inquilino
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Factura */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <div>
            <h3 className="font-semibold text-sereno-dark text-lg">Factura</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Importes del «Resumen de la factura», sin IVA.
              {lectura.estado !== 'ok' && ' De partida, los de la de Lucera de marzo de 2026.'}
            </p>
          </div>

          <div className="space-y-2">
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setArrastrando(true);
              }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={soltar}
              className={`flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 cursor-pointer transition-colors ${
                arrastrando ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
              }`}
            >
              {lectura.estado === 'leyendo' ? (
                <Loader2 size={20} className="text-blue-600 animate-spin shrink-0" />
              ) : (
                <FileUp size={20} className="text-blue-600 shrink-0" />
              )}
              <div className="min-w-0 text-sm">
                <p className="font-medium text-slate-700 truncate">
                  {lectura.estado === 'leyendo' ? `Leyendo ${lectura.nombre}…` : 'Sube el PDF de la factura'}
                </p>
                <p className="text-xs text-slate-400">Haz clic o arrástralo aquí y los importes se rellenan solos</p>
              </div>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="sr-only"
                disabled={lectura.estado === 'leyendo'}
                onChange={(e) => {
                  leerPdf(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>

            {lectura.estado === 'ok' && (
              <div className="flex gap-2 text-sm text-slate-600">
                <Check size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                <p>
                  Leída la factura
                  {lectura.factura.comercializadora && ` de ${lectura.factura.comercializadora}`}
                  {lectura.factura.periodo && ` de ${lectura.factura.periodo}`}
                  {lectura.factura.desde && lectura.factura.hasta && ` (${lectura.factura.desde} – ${lectura.factura.hasta})`}
                  {lectura.factura.numero && `, nº ${lectura.factura.numero}`}
                  {lectura.factura.total !== null && `. Total a pagar: ${eur(lectura.factura.total)}`}.
                </p>
              </div>
            )}
            {lectura.estado === 'ok' && lectura.factura.avisos.length > 0 && (
              <ul className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1 text-xs text-amber-800">
                {lectura.factura.avisos.map((a) => (
                  <li key={a} className="flex gap-1.5">
                    <AlertTriangle size={14} className="shrink-0 mt-px" /> {a}
                  </li>
                ))}
              </ul>
            )}
            {lectura.estado === 'error' && (
              <p className="flex gap-2 text-sm text-red-600">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {lectura.mensaje}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Costes fijos · a partes iguales
            </h4>
            <div className="space-y-2">
              <CampoNumero label="Potencia" value={factura.potencia} onChange={campo('potencia')} suffix="€" />
              <CampoNumero label="Cuota comercializadora" value={factura.cuota} onChange={campo('cuota')} suffix="€" />
              <CampoNumero
                label="Otros costes"
                value={factura.otros}
                onChange={campo('otros')}
                suffix="€"
                hint="Contador y bono social"
              />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Costes por consumo · según kWh
            </h4>
            <div className="space-y-2">
              <CampoNumero label="Energía" value={factura.energia} onChange={campo('energia')} suffix="€" />
              <CampoNumero label="Impuesto eléctrico" value={factura.impuesto} onChange={campo('impuesto')} suffix="€" />
              <CampoNumero
                label="kWh facturados"
                value={factura.kwh}
                onChange={campo('kwh')}
                suffix="kWh"
                hint="Suma de P1, P2 y P3"
              />
            </div>
          </div>

          <CampoNumero label="IVA" value={factura.iva} onChange={campo('iva')} suffix="%" />

          <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
            <Row label="Costes fijos" value={eur(r.fijos)} />
            <Row label="Costes por consumo" value={eur(r.variables)} />
            <Row label="Base imponible" value={eur(r.base)} />
            <Row label={`IVA (${num(factura.iva).toLocaleString('es-ES')} %)`} value={eur(r.iva)} />
            <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold text-sereno-dark">
              <span>Total factura</span>
              <span className="tabular-nums">{eur(r.total)}</span>
            </div>
            {r.precioKwh > 0 && (
              <p className="text-xs text-slate-400 pt-1">
                Precio del kWh (energía e impuesto, sin IVA):{' '}
                {r.precioKwh.toLocaleString('es-ES', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} €
              </p>
            )}
          </div>
        </div>

        {/* Contadores */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <div>
            <h3 className="font-semibold text-sereno-dark text-lg">Contadores</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              El consumo de cada inquilino es la lectura actual de su contador menos la anterior.
            </p>
          </div>

          {carga === 'cargando' && (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" /> Cargando las lecturas guardadas…
            </p>
          )}
          {carga === 'error' && (
            <p className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              No se han podido cargar las lecturas guardadas. Puedes calcular el reparto, pero no guardarlo; recarga la
              página para intentarlo otra vez.
            </p>
          )}
          {primeraVez && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-3 text-sm text-slate-600">
              <p>
                <strong>Primera vez:</strong> escribe en «Anterior» la lectura inicial de cada contador y guárdala. A
                partir de ahí, cada mes solo tendrás que escribir la actual.
              </p>
              <button
                type="button"
                onClick={guardarIniciales}
                disabled={guardando || hayInvalidos || sinAnterior.length > 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Save size={15} /> Guardar lecturas iniciales
              </button>
            </div>
          )}

          {aviso?.donde === 'contadores' && <MensajeAviso aviso={aviso} />}

          {carga !== 'cargando' && (
            <div className="space-y-3">
              {inquilinos.map((q, i) => {
                const c = consumos[i];
                return (
                  <div key={q.id} className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={q.nombre}
                        onChange={(e) => cambiarInquilino(q.id, { nombre: e.target.value })}
                        aria-label="Nombre del inquilino"
                        className="flex-1 min-w-0 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      />
                      <label
                        className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none shrink-0"
                        title="Si paga su parte de la potencia, la cuota, el contador y el bono social"
                      >
                        <input
                          type="checkbox"
                          checked={q.pagaFijos}
                          onChange={(e) => cambiarInquilino(q.id, { pagaFijos: e.target.checked })}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Paga fijos
                      </label>
                      <button
                        type="button"
                        onClick={() => quitarInquilino(q.id)}
                        disabled={n <= 1}
                        title="Quitar inquilino"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <span className="block text-xs text-slate-500 mb-1">Anterior</span>
                        <EntradaNumero
                          value={q.anterior}
                          onChange={(v) => cambiarInquilino(q.id, { anterior: v })}
                          ariaLabel={`Lectura anterior de ${nombreDe(i)}`}
                        />
                      </div>
                      <div>
                        <span className="block text-xs text-slate-500 mb-1">Actual</span>
                        <EntradaNumero
                          value={q.actual}
                          onChange={(v) => cambiarInquilino(q.id, { actual: v })}
                          ariaLabel={`Lectura actual de ${nombreDe(i)}`}
                        />
                      </div>
                      <div className="text-right pb-1.5 min-w-[4.5rem]">
                        <span className="block text-xs text-slate-500">Consumo</span>
                        <span
                          className={`text-sm font-semibold tabular-nums ${
                            c !== null && c < 0 ? 'text-red-600' : 'text-sereno-dark'
                          }`}
                        >
                          {c === null ? '—' : kwhTxt(c)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={anadirInquilino}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                <Plus size={16} /> Añadir inquilino
              </button>
            </div>
          )}

          <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
            <Row
              label={`Costes fijos entre ${r.pagadores} ${r.pagadores === 1 ? 'persona' : 'personas'}`}
              value={
                r.pagadores === 0
                  ? '—'
                  : `${eur(r.fijos / r.pagadores)} + IVA${r.pagadores > 1 ? ' cada una' : ''}`
              }
            />
            <Row
              label="Consumo de los contadores"
              value={
                r.kwhFactura > 0
                  ? `${r.kwhTotal.toLocaleString('es-ES', { maximumFractionDigits: 2 })} de ${kwhTxt(r.kwhFactura)}`
                  : kwhTxt(r.kwhTotal)
              }
            />
          </div>

          {repartoListo && r.hayDiferencia && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3 text-sm">
              <div className="flex gap-2 text-amber-800">
                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                <p>
                  {r.diferenciaKwh > 0
                    ? `Los contadores suman ${kwhTxt(r.kwhTotal)} y la factura recoge ${kwhTxt(r.kwhFactura)}: quedan ${kwhTxt(r.diferenciaKwh)} sin asignar.`
                    : `Los contadores suman ${kwhTxt(r.kwhTotal)}, ${kwhTxt(-r.diferenciaKwh)} más de lo que recoge la factura (${kwhTxt(r.kwhFactura)}). La energía se reparte en proporción a lo que ha gastado cada uno.`}
                </p>
              </div>
              {r.diferenciaKwh > 0 && (
                <div>
                  <label className="block text-xs font-medium text-amber-800 mb-1">
                    ¿Cómo se reparten esos {kwhTxt(r.diferenciaKwh)}?
                  </label>
                  <select
                    value={modoDiferencia}
                    onChange={(e) => setModoDiferencia(e.target.value as RepartoDiferencia)}
                    className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                  >
                    <option value="consumo">En proporción a lo que ha gastado cada uno</option>
                    <option value="iguales">A partes iguales entre los que pagan fijos (zonas comunes)</option>
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Resultado */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
        <h3 className="font-semibold text-sereno-dark text-lg">Lo que paga cada inquilino</h3>

        {hayInvalidos ? (
          <p className="text-sm text-red-600">Hay una casilla en rojo que no es un número: corrígela para ver el reparto.</p>
        ) : r.pagadores === 0 ? (
          <p className="text-sm text-red-600">
            Nadie paga los costes fijos: marca «Paga fijos» en al menos un inquilino.
          </p>
        ) : negativos.length > 0 ? (
          <p className="text-sm text-red-600">
            La lectura actual de {negativos.join(', ')} es menor que la anterior: revísala.
          </p>
        ) : sinAnterior.length > 0 ? (
          <p className="text-sm text-slate-500">
            Falta la lectura anterior de {sinAnterior.join(', ')}. Si es la primera vez, escribe la lectura inicial de su
            contador.
          </p>
        ) : sinActual.length > 0 ? (
          <p className="text-sm text-slate-500">Escribe la lectura actual de {sinActual.join(', ')} para ver el reparto.</p>
        ) : r.kwhTotal === 0 ? (
          <p className="text-sm text-slate-500">Ningún contador ha marcado consumo entre las dos lecturas.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {r.filas.map((f) => (
                <div key={f.id} className="rounded-xl border border-slate-200 p-5 space-y-3">
                  <div>
                    <h4 className="font-semibold text-sereno-dark truncate" title={f.nombre}>
                      {f.nombre}
                    </h4>
                    <p className="text-xs text-slate-400 truncate">
                      {kwhTxt(f.kwh)} · {(f.pctConsumo * 100).toLocaleString('es-ES', { maximumFractionDigits: 1 })} %
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-sereno-dark tabular-nums">{eur(f.total)}</p>
                  <div className="space-y-1.5 text-sm">
                    <Row label="Costes fijos" value={f.pagaFijos ? eur(f.fijo) : 'No paga'} />
                    <Row label="Consumo" value={eur(f.consumo)} />
                    <Row label="IVA" value={eur(f.iva)} />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Check size={16} className="text-emerald-600" />
              Entre todos suman {eur(r.sumaFilas)}, el total de la factura.
            </div>

            {carga === 'ok' && (
              <div className="border-t border-slate-200 pt-5 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-slate-600 mb-1">Periodo de la factura</label>
                    <input
                      type="text"
                      value={periodo}
                      onChange={(e) => setPeriodo(e.target.value)}
                      placeholder="marzo 2026"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={cerrarMes}
                    disabled={guardando || !periodo.trim() || yaGuardado}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {yaGuardado ? 'Ya guardado' : 'Guardar lecturas'}
                  </button>
                </div>
                <p className="text-xs text-slate-400">
                  {yaGuardado
                    ? `Las lecturas de ${ultimoCierre?.periodo} ya están en el historial. Para corregirlas, deshaz ese mes.`
                    : 'Al guardar, la lectura actual de cada contador pasa a ser la anterior del mes que viene, y el reparto queda en el historial.'}
                </p>
              </div>
            )}
          </>
        )}

        {aviso?.donde === 'reparto' && <MensajeAviso aviso={aviso} />}
      </div>

      {/* Historial */}
      {guardadas && guardadas.cierres.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
          <h3 className="font-semibold text-sereno-dark text-lg">Historial</h3>
          {[...guardadas.cierres].reverse().map((c, i) => (
            <div key={c.id} className="rounded-lg border border-slate-200 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-sereno-dark">{c.periodo}</h4>
                  <p className="text-xs text-slate-400">
                    {c.desde && c.hasta && `${c.desde} – ${c.hasta} · `}
                    Factura de {eur(c.totalFactura)} · guardado el {new Date(c.guardado).toLocaleDateString('es-ES')}
                  </p>
                </div>
                {i === 0 && (
                  <button
                    type="button"
                    onClick={deshacer}
                    disabled={guardando}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-600 disabled:opacity-50 shrink-0"
                  >
                    <Undo2 size={15} /> Deshacer
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-slate-500 text-left">
                      <th className="font-medium pb-1.5">Inquilino</th>
                      <th className="font-medium pb-1.5 text-right">Lecturas</th>
                      <th className="font-medium pb-1.5 text-right">Consumo</th>
                      <th className="font-medium pb-1.5 text-right">Pagó</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.lineas.map((l) => (
                      <tr key={l.contadorId} className="border-t border-slate-100">
                        <td className="py-1.5 text-slate-700">
                          {l.nombre}
                          {l.pagaFijos === false && <span className="ml-1.5 text-xs text-slate-400">sin fijos</span>}
                        </td>
                        <td className="py-1.5 text-right text-slate-500 tabular-nums whitespace-nowrap">
                          {lecturaTxt(l.anterior)} → {lecturaTxt(l.actual)}
                        </td>
                        <td className="py-1.5 text-right text-slate-700 tabular-nums whitespace-nowrap">{kwhTxt(l.kwh)}</td>
                        <td className="py-1.5 text-right font-medium text-sereno-dark tabular-nums whitespace-nowrap">
                          {eur(l.importe)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 rounded-xl border border-blue-100 p-5 space-y-3 text-sm text-slate-600">
        <div className="flex items-center gap-2 text-sereno-dark font-semibold">
          <Info size={18} /> ¿Cómo se reparte?
        </div>
        <p>
          <strong>Costes fijos</strong> (potencia contratada, cuota, alquiler del contador y bono social): se pagan
          aunque no se gaste nada, así que se dividen a partes iguales entre los inquilinos que tienen marcado «Paga
          fijos» (ahora, {r.pagadores}). Un local vacío puede seguir leyendo su contador sin pagarlos.
        </p>
        <p>
          <strong>Costes por consumo</strong> (energía e impuesto eléctrico): cada inquilino paga la parte que le toca
          según los kWh que ha marcado su contador entre la lectura anterior y la actual.
        </p>
        <p>
          <strong>IVA</strong>: se aplica a la parte de cada uno. Los céntimos se redondean para que la suma de todos
          coincida exactamente con el total de la factura.
        </p>
      </div>
    </div>
  );
}

// --- Small components ---
function MensajeAviso({ aviso }: { aviso: NonNullable<Aviso> }) {
  return (
    <p className={`flex gap-2 text-sm ${aviso.tipo === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}>
      {aviso.tipo === 'ok' ? (
        <Check size={16} className="shrink-0 mt-0.5" />
      ) : (
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      )}
      {aviso.texto}
    </p>
  );
}

function EntradaNumero({
  value,
  onChange,
  suffix,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  suffix?: string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const invalido = leer(value) === null;
  return (
    <div className="relative">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        aria-invalid={invalido}
        className={`w-full rounded-lg border px-3 py-2 ${suffix ? 'pr-11' : ''} text-sm text-right tabular-nums outline-none focus:ring-1 ${
          invalido
            ? 'border-red-400 focus:border-red-500 focus:ring-red-500'
            : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
        }`}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}

function CampoNumero({
  label,
  value,
  onChange,
  suffix,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suffix: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <label className="block text-sm font-medium text-slate-600">{label}</label>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
      <div className="w-36 shrink-0">
        <EntradaNumero value={value} onChange={onChange} suffix={suffix} ariaLabel={label} />
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-700 tabular-nums">{value}</span>
    </div>
  );
}
